/**
 * Variant C design tokens — the colour, type, and motion vocabulary
 * the Loom/Spine UI is built from.
 *
 * Layered on top of ``brand.ts``:
 *   - Brand purples (primary / mid / light) come from ``brand.ts`` so
 *     the Indegene white-label still works without a fork.
 *   - Ink/paper/line/status tokens are net-new — Variant C's
 *     depth-of-field treatment needs more granularity than Fluent's
 *     neutral ramps offer.
 *   - Typography: Inter-first body (aligned with globals.css
 *     ``--font-sans``), Century Gothic display, SF Mono. ``typeScale``
 *     is the shared size vocabulary for chat surfaces.
 *
 * SRP: this module is a pure data export. No styling logic, no
 * component code, no React imports. Components import these values
 * and compose them via ``makeStyles``.
 *
 * Why not just use Fluent's ``tokens``: Fluent's neutral ramp is too
 * cool for the JSX's purple-tinted ink scale, and Fluent has no
 * direct equivalent for the ``hot`` (active-running) signal colour.
 * We use Fluent tokens where they fit (gaps, radii, font weights)
 * and our own where they don't (colour, custom typography).
 */

import { brand } from "../config/brand";

// ---------------------------------------------------------------------------
// Brand re-exports — single source of truth lives in brand.ts
// ---------------------------------------------------------------------------

export const brandColors = {
  /** Primary purple — accent, primary buttons, active states. */
  primary: brand.colorPrimary,
  /** Mid purple — secondary accents, scope chips, ambient glow. */
  mid: brand.colorPrimaryMid,
  /** Light purple — active-row backgrounds, chip tints. */
  light: brand.colorPrimaryLight,
  /** Tint — hero card gradient start (landingpage.txt §2). */
  tint: "#FAF7FE",
  /** Translucent mid — used for ambient pulse/glow effects. */
  glow: "rgba(131, 103, 199, 0.25)",
} as const;

// ---------------------------------------------------------------------------
// Ink scale — five-step purple-tinted neutral ramp
// ---------------------------------------------------------------------------

export const inkColors = {
  /** Deepest text colour — headings, primary content. */
  ink: "#0F0A1E",
  /** Body text — paragraphs, secondary headings. */
  ink2: "#2B2438",
  /** Mid emphasis — subtitles, captions, hover targets. */
  ink3: "#5A5170",
  /** Tertiary — placeholder text, disabled labels, meta. */
  ink4: "#8C8599",
  /** Faintest — dividers, hairlines, hint glyphs. */
  ink5: "#BDB7C7",
} as const;

// ---------------------------------------------------------------------------
// Surface colours — paper / line stops
// ---------------------------------------------------------------------------

export const surfaceColors = {
  /** Default canvas background — faintly purple off-white. */
  paper: "#FBF9FF",
  /** Inset surfaces — secondary cards, banners. */
  paperDim: "#F5F1FA",
  /** Hairline divider — default 1px borders. */
  line: "#E7E0F2",
  /** Stronger divider — node dots, button outlines. */
  lineStrong: "#D4CAE6",
} as const;

// ---------------------------------------------------------------------------
// Status colours — semantic signal vocabulary
// ---------------------------------------------------------------------------

export const statusColors = {
  /** Active / running — pulsing node, "drafting now". */
  hot: "#FF5E3A",
  /** Done / pass — completed sections, healthy verdicts. */
  cool: "#26DF9B",
  /** Caution / medium severity — needs review. */
  warn: "#F5A524",
  /** Blocker / critical — refuse-to-file, low quality. */
  danger: "#E11D48",
} as const;

// ---------------------------------------------------------------------------
// Kind colours — per-EventKind accent vocabulary for spine tag pills,
// event legend strip, and timeline row glyphs. Sourced from the
// variant-c-scenes.jsx design prototype's inline colors. Centralised
// here so the hex lives in ONE place; components reference by name.
// ---------------------------------------------------------------------------

export const kindColors = {
  /** `🌐 web` — external literature / internet search. */
  web: "#06B6D4",
  /** `📎 Word` — writer-initiated Word doc edit. */
  wordEdit: "#1565C0",
  /** `📚 library` — library content swap (uploads / removals). */
  library: "#D97706",
} as const;

/**
 * Audit-trail kind palette — the coarse Run/Steer/Comment/Edit/Evidence/Access
 * vocabulary the audit timeline colours by. Distinct from `kindColors` (which
 * is the activity-event palette); the keys here are the audit `colorKey`s the
 * timeline turns into `--audit-<key>-bg/-fg` CSS variables.
 */
export const auditKindColors = {
  run: "#2563EB",
  steer: "#7C3AED",
  comment: "#0891B2",
  edit: "#D97706",
  evidence: "#059669",
  access: "#6B7280",
} as const;

// ---------------------------------------------------------------------------
// Doctype chip colours — per-doctype background/text for HomeDocCard badges
// ---------------------------------------------------------------------------

export const doctypeChipColors: Record<string, { bg: string; fg: string }> = {
  CSR: { bg: "#E8F4FD", fg: "#0078D4" },
  PSUR: { bg: "#FFF3E0", fg: "#E65100" },
  DSUR: { bg: "#E8F5E9", fg: "#2E7D32" },
  CER: { bg: "#E8F5E9", fg: "#2E7D32" },
} as const;

export const doctypeChipFallback = { bg: surfaceColors.paperDim, fg: inkColors.ink3 } as const;

export function doctypeChipColorFor(code: string | null) {
  if (!code) return doctypeChipFallback;
  return doctypeChipColors[code as keyof typeof doctypeChipColors] ?? doctypeChipFallback;
}

// ---------------------------------------------------------------------------
// Gate palette — colours the Selection picker's gate packets. Ordered
// structurally (by position in the doctype's ``gates[]`` array) rather
// than by name, so the picker is config-driven: a YAML that renames
// "configure" to "setup" still lands on the same first-position colour.
// Palette cycles for doctypes with more gates than entries.
//
// Current mapping matches the Spec 19 v2 scenes prototype for
// CSR/ICH-E3: position 0 (Configure) = brand-mid purple, position 1
// (Assemble) = hot orange, position 2 (Finalize) = cool green.
// ---------------------------------------------------------------------------

export const gatePalette: readonly string[] = [brandColors.mid, statusColors.hot, statusColors.cool] as const;

/** Picks a palette colour for a gate at ``index`` (0-based position
 *  in the doctype's gates array). Cycles via modulo when the doctype
 *  declares more gates than palette entries — no crash, no branching. */
export function gateColorAt(index: number): string {
  if (index < 0) return gatePalette[0];
  return gatePalette[index % gatePalette.length];
}

// ---------------------------------------------------------------------------
// Typography
// ---------------------------------------------------------------------------

export const fonts = {
  /** Body text — readable at 11-13px in a Word task pane. Inter-first,
   *  matching the app shell's ``--font-sans`` (globals.css). Inter is
   *  not webfont-loaded, so Segoe UI is the effective render on most
   *  machines — same as the previous Lato-first stack, which also fell
   *  back. The unification removes the shell-vs-bubble declaration
   *  mismatch without a visual regression. */
  body: "'Inter', 'Segoe UI', sans-serif",
  /** Display headings + doc codes — gives products a regulatory feel. */
  display: "'Century Gothic', 'Futura', 'Lato', sans-serif",
  /** Section numbers, hashes, terminal-style ambient text. */
  mono: "'SF Mono', 'Consolas', monospace",
} as const;

// ---------------------------------------------------------------------------
// Type + shape scale — shared sizing vocabulary for the chat surfaces
// ---------------------------------------------------------------------------
//
// # DECISION: chat surfaces size text from ``typeScale`` and shape
// #   bubbles from ``bubbleShape`` instead of per-component px literals.
// # WHY: before 2026-07-14 every bubble/banner/composer size was a
// #      hardcoded string (12.5px body, 9px labels, 10px meta) that had
// #      drifted apart across MessageBubble / TimelineView / CommandRail.
// #      A single scale keeps composer text the same size as the bubble
// #      it becomes, and makes the next resize a one-line change.
// # DATE: 2026-07-14

export const typeScale = {
  /** Chat body copy — user text, assistant markdown, composer input,
   *  greeting, streaming draft. */
  body: "13px",
  /** Line height paired with ``body``. Unitless so it scales. */
  bodyLineHeight: 1.55,
  /** Markdown headings inside assistant bubbles. */
  heading: "13.5px",
  /** Speaker labels above bubbles ("You · 09:33"), day pills,
   *  show-more buttons. */
  label: "10px",
  /** Meta strips — system notes, tool rows. */
  meta: "10.5px",
  /** Inline/pre code + tables inside assistant markdown. */
  code: "11.5px",
} as const;

export const bubbleShape = {
  /** Assistant card — tight top-LEFT corner, nearest its label. */
  assistantRadius: "4px 10px 10px 10px",
  /** User bubble — mirrored: tight top-RIGHT corner, nearest its label. */
  userRadius: "10px 4px 10px 10px",
  /** Bubble inner padding, shared by user + assistant surfaces. */
  padding: "8px 12px",
  /** Soft lift shadow for the two speech surfaces. */
  shadow: "0 1px 2px rgba(15, 10, 30, 0.06)",
} as const;

// ---------------------------------------------------------------------------
// Motion — durations + easings, single source for prefers-reduced-motion
// ---------------------------------------------------------------------------

export const motion = {
  /** Active-node pulse cycle. */
  pulseDurationMs: 1600,
  /** Ambient mote drift along the spine thread. */
  moteDurationMs: 3000,
  /** Top-bar heartbeat sweep during a live run. */
  heartbeatDurationMs: 2400,
  /** Generic state transitions — fade, opacity, depth-of-field recede. */
  transitionMs: 300,
  /** ``@media (prefers-reduced-motion: reduce)`` predicate string. */
  reducedMotionQuery: "(prefers-reduced-motion: reduce)",
} as const;

// ---------------------------------------------------------------------------
// Chat surface — Variant D additions (Apple-Photos-style history view)
// ---------------------------------------------------------------------------
//
// # DECISION: Variant D tokens are added as a NAMED bundle, not merged
// #   into surfaceColors / statusColors.
// # WHY: Variant D's palette uses warm-paper backgrounds (#FAF8F2) and
// #      cool-blue running indicators (#3B82F6). Existing Variant C
// #      surfaces use purple-tinted paper (#FBF9FF) and cool-green
// #      done indicators (#26DF9B). The semantics differ: Variant C's
// #      ``cool`` means "completed", the chat surface's ``toolRunning``
// #      means "in flight". Overwriting the existing values would
// #      silently change every existing usage. Adding a separate
// #      ``chatSurface`` bundle keeps both vocabularies coherent and
// #      makes the chat-vs-loom visual lineage explicit at every
// #      consumer site (`chatSurface.paperWarm` reads differently from
// #      `surfaceColors.paper`).
// # DATE: 2026-04-26
//
// Source: Variant D design spec §6.1 (DESIGN_SPEC Variant D.md).
// Hex values are intentionally 1:1 with the spec — components import
// these tokens; the spec is the SSOT.

export const chatSurface = {
  /** Warm-paper background for chat scroll, library bar, top bar. */
  paperWarm: "#FAF8F2",
  /** Slightly inset surface — tool-row backgrounds, secondary cards. */
  paperWarmDim: "#F2EFE8",
  /** Soft hairline divider — between bubbles, inside expanded day groups.
   *  Also used as the Asthra bubble's three soft sides (top/right/bottom). */
  lineSoft: "#F0EBE2",
  /** Tool-running status dot. NOT the same as ``statusColors.cool``
   *  (which means "completed"). Use this for in-flight signals only. */
  toolRunning: "#3B82F6",
} as const;
//
// # DECISION: Asthra bubble uses WHITE background + 2px brand-mid LEFT
// #   border, NOT a purple-tint background.
// # WHY: handoff bundle's ``DESIGN_SPEC Variant D.md`` §3.1 wrote
// #      "Light brand-purple tint (#F5F0FF), brand-purple border" but
// #      the README explicitly defers to the JSX as ground truth, and
// #      ``variant-d.jsx`` AssistantMsg uses ``background:'#fff'`` +
// #      ``borderLeft:'2px solid VD.brandMid'`` + 1px lineSoft border on
// #      the other three sides. The brand-purple LEFT accent is the
// #      "agent voice" signature; a fully tinted bubble obscures it.
// #      The earlier ``bubbleTint`` and ``bubbleBorder`` tokens have
// #      been removed — they were modelled on the history-tab variant
// #      that drifted from the canonical live-chat treatment.
// # DATE: 2026-04-26

// ---------------------------------------------------------------------------
// Aggregate export — one import for everything in components
// ---------------------------------------------------------------------------

export const variantCTokens = {
  brand: brandColors,
  ink: inkColors,
  surface: surfaceColors,
  status: statusColors,
  chat: chatSurface,
  fonts,
  typeScale,
  bubbleShape,
  motion,
} as const;

export type VariantCTokens = typeof variantCTokens;
